import React from 'react'
import PropTypes from 'prop-types'
import { DragDropContext, Droppable } from 'react-beautiful-dnd'

import { digObject } from '@campaignhub/javascript-utils'

import Box from '../Box'

import useCustomizableDashboard from './hooks/useCustomizableDashboard'
import AddDashboardModuleModal from './modals/AddDashboardModuleModal'

import DashboardControls from './DashboardControls'
import ModuleRenderer from './ModuleRenderer'

import CustomizableDashboardContext from '../../contexts/customizableDashboardContext'

import styles from './styles.scss'

const callbacks = (component, props, setState) => {
  const { callbacks: { toggleEditDashboard } } = props

  const componentCallbacks = {
    DashboardControls: {
      showAddDashboardModuleModal: () => setState({ showAddDashboardModuleModal: true }),
      toggleEditDashboard,
    },
  }

  return componentCallbacks[component] || {}
}

const CustomizableDashboard = (props) => {
  const customizableDashboardPayload = useCustomizableDashboard(props)

  const {
    activeLayout,
    availableModules,
    callbacks: { addModule, onDragEnd },
    columns,
    editing,
    setState,
    showAddDashboardModuleModal,
    state,
  } = customizableDashboardPayload

  return (
    <CustomizableDashboardContext.Provider value={customizableDashboardPayload}>
      <Box flexDirection="column">
        {editing && (
          <DashboardControls
            callbacks={callbacks('DashboardControls', props, setState)}
          />
        )}

        <DragDropContext
          onDragEnd={result => onDragEnd(result, state, setState)}
        >
          <Box className={styles.root} flexDirection={['column', 'column', 'row']}>
            {columns.map((column) => {
              const { id, component } = column
              const columnData = digObject(activeLayout, `columns.${id}`)

              if (!columnData || !columnData.modules) return null
              const { modules } = columnData

              return (
                <Droppable droppableId={id} key={id}>
                  {(provided, snapshot) => React.cloneElement(component, {
                    draggablePlaceholder: provided.placeholder,
                    id,
                    modules,
                    ref: provided.innerRef,
                    ...provided.droppableProps,
                    ...snapshot,
                  })}
                </Droppable>
              )
            })}
          </Box>
        </DragDropContext>
      </Box>

      <AddDashboardModuleModal
        availableModules={availableModules}
        callbacks={{
          addModule: payload => addModule(payload, props, state, setState),
          closeModal: () => setState({ showAddDashboardModuleModal: false }),
        }}
        columns={columns}
        showModal={showAddDashboardModuleModal}
      />
    </CustomizableDashboardContext.Provider>
  )
}

CustomizableDashboard.propTypes = {
  props: PropTypes.shape({
    availableModules: PropTypes.object,
    callbacks: PropTypes.shape({
      toggleEditDashboard: PropTypes.func.isRequired,
    }).isRequired,
    columns: PropTypes.arrayOf(PropTypes.shape({
      id: PropTypes.string,
      component: PropTypes.object,
    })).isRequired,
    customizedLayout: PropTypes.shape({
      columns: PropTypes.object.isRequired,
    }),
    dashboardKey: PropTypes.string,
    defaultLayout: PropTypes.shape({
      columns: PropTypes.object.isRequired,
    }).isRequired,
    editing: PropTypes.bool,
  }),
}

CustomizableDashboard.ModuleRenderer = ModuleRenderer

export default CustomizableDashboard